import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabaseClient, useSession } from "@supabase/auth-helpers-react";
import { toast } from "sonner";
import { Users, Wrench, Tags, Settings2, Loader2 } from "lucide-react";
import { ToolsManagement } from "@/components/admin/ToolsManagement";
import { UsersManagement } from "@/components/admin/UsersManagement";
import { CategoriesManagement } from "@/components/admin/CategoriesManagement";
import { DashboardLayout } from "@/components/admin/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { motion } from "framer-motion";

const Admin = () => {
  const [activeTab, setActiveTab] = useState<'tools' | 'users' | 'settings' | 'categories'>('tools');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTools: 0,
    totalCategories: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<'admin' | 'moderator' | 'user'>('user');
  const supabase = useSupabaseClient();
  const session = useSession();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!session?.user?.id) return;
      
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        return;
      }

      if (roles) {
        setUserRole(roles.role as 'admin' | 'moderator' | 'user');
      }
    };

    fetchUserRole();
  }, [session, supabase]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          { count: usersCount }, 
          { count: toolsCount },
          { count: categoriesCount }
        ] = await Promise.all([
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('tools').select('*', { count: 'exact', head: true }),
          supabase.from('categories').select('*', { count: 'exact', head: true }),
        ]);

        setStats({
          totalUsers: usersCount || 0,
          totalTools: toolsCount || 0,
          totalCategories: categoriesCount || 0,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast.error("حدث خطأ أثناء تحميل الإحصائيات");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [supabase]);

  // Get menu items based on user role
  const getMenuItems = () => {
    const items = [
      { id: 'tools', title: 'الأدوات', icon: 'LayoutDashboard' },
      { id: 'categories', title: 'التصنيفات', icon: 'Tags' },
    ];

    if (userRole === 'admin') {
      items.push(
        { id: 'users', title: 'المستخدمين', icon: 'Users' },
        { id: 'settings', title: 'الإعدادات', icon: 'Settings' }
      );
    }

    return items;
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Reset active tab if user doesn't have access to it
  useEffect(() => {
    if (userRole !== 'admin' && (activeTab === 'users' || activeTab === 'settings')) {
      setActiveTab('tools');
    }
  }, [userRole, activeTab]);

  return (
    <AuthGuard allowModerator>
      <DashboardLayout 
        activeTab={activeTab} 
        onTabChange={(tab: any) => setActiveTab(tab)}
        menuItems={getMenuItems()}
      >
        {/* Stats Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {userRole === 'admin' && (
            <motion.div variants={item}>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 hover:shadow-lg transition-all duration-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-500" />
                    إجمالي المستخدمين
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {stats.totalUsers}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          <motion.div variants={item}>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 hover:shadow-lg transition-all duration-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-green-500" />
                  إجمالي الأدوات
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {stats.totalTools}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 hover:shadow-lg transition-all duration-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <Tags className="h-5 w-5 text-purple-500" />
                  إجمالي التصنيفات
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.totalCategories}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Content */}
        <motion.div 
          className="bg-card rounded-lg shadow-sm border p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {activeTab === 'tools' && <ToolsManagement />}
          {activeTab === 'categories' && <CategoriesManagement />}
          {userRole === 'admin' && activeTab === 'users' && <UsersManagement />}
          {userRole === 'admin' && activeTab === 'settings' && (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-primary flex items-center gap-2">
                <Settings2 className="h-6 w-6" />
                الإعدادات
              </h2>
              <p className="text-muted-foreground">قريباً...</p>
            </div>
          )}
        </motion.div>
      </DashboardLayout>
    </AuthGuard>
  );
};

export default Admin;